import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { PlusIcon } from '@heroicons/react/24/outline'
import { api, usePermission, type Beneficiary } from '@lipaflow/shared'
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  FilterChips,
  Modal,
  PageHeader,
  formatDate,
  type BadgeVariant,
  type ColumnDef,
  type FilterChipOption,
} from '@lipaflow/ui'

type Kind = 'commendation' | 'positive_behavior' | 'negative_behavior' | 'disciplinary' | 'other'
type Severity = 'minor' | 'moderate' | 'major'

interface Incident {
  id: string
  beneficiary_id: string
  beneficiary_name: string | null
  beneficiary_reference: string | null
  incident_type: Kind
  category: string | null
  description: string
  severity: Severity | null
  action_taken: string | null
  points: number | null
  incident_date: string | null
  is_visible_to_parent: boolean
}

// The API's own kinds and severities; the old page sent 'positive'/'negative'
// and 'low'…'critical', which the API rejects.
const KIND: Record<Kind, { label: string; variant: BadgeVariant }> = {
  commendation: { label: 'Praise', variant: 'success' },
  positive_behavior: { label: 'Good behaviour', variant: 'success' },
  negative_behavior: { label: 'Concern', variant: 'warning' },
  disciplinary: { label: 'Disciplinary', variant: 'warning' },
  other: { label: 'Note', variant: 'neutral' },
}
const KIND_ORDER: Kind[] = ['commendation', 'positive_behavior', 'negative_behavior', 'disciplinary', 'other']
const SEVERITY: Record<Severity, string> = { minor: 'Minor', moderate: 'Moderate', major: 'Serious' }
const needsSeverity = (k: Kind) => k === 'negative_behavior' || k === 'disciplinary'
const SUGGESTIONS: Record<Kind, string[]> = {
  commendation: ['Academic excellence', 'Leadership', 'Helping others', 'Sports', 'Arts'],
  positive_behavior: ['Punctuality', 'Participation', 'Kindness', 'Improvement'],
  negative_behavior: ['Lateness', 'Disruption', 'Incomplete work', 'Uniform', 'Phone use'],
  disciplinary: ['Fighting', 'Bullying', 'Cheating', 'Property damage', 'Truancy'],
  other: [],
}

type Show = '' | Kind
const SHOW: FilterChipOption<Show>[] = [{ key: '', label: 'All' }, ...KIND_ORDER.map((k) => ({ key: k as Show, label: KIND[k].label }))]
const field = 'w-full rounded-button border border-hairline bg-surface px-3 py-2 text-body-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent'
const apiError = (e: unknown) => (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
const PAGE_SIZE = 50

/**
 * Behaviour — praise, concerns and disciplinary matters per student.
 *
 * Rebuilt on the API's actual contract. The old page posted fields the API
 * doesn't accept (student_id, type) so nothing could be recorded, read
 * fields it doesn't return, and had no way to correct a record.
 */
export default function DisciplinePage() {
  const canView = usePermission('discipline:view')
  const canManage = usePermission('discipline:manage')
  const [show, setShow] = useState<Show>('')
  const [page, setPage] = useState(1)
  const [recording, setRecording] = useState(false)
  const [open, setOpen] = useState<Incident | null>(null)

  const list = useQuery({
    queryKey: ['discipline', 'incidents', show, page],
    queryFn: async () => (await api.get<{ items: Incident[]; total: number }>('/education/discipline/incidents', {
      params: { page, page_size: PAGE_SIZE, incident_type: show || undefined },
    })).data,
    enabled: canView,
  })

  if (!canView) return <EmptyState title="Behaviour records are for teachers and school leaders" description="Ask your admin if you need to see them." className="py-16" />

  const columns: ColumnDef<Incident>[] = [
    {
      key: 'student', header: 'Student',
      cell: (i) => (
        <div>
          <div className="font-medium text-ink">{i.beneficiary_name ?? 'Student'}</div>
          <div className="text-caption text-ink-muted font-mono">{i.beneficiary_reference}</div>
        </div>
      ),
    },
    {
      key: 'what', header: 'What happened',
      cell: (i) => (
        <div className="min-w-0">
          <div className="text-body-sm text-ink truncate">{i.category || KIND[i.incident_type]?.label}</div>
          <div className="text-caption text-ink-muted truncate max-w-md">{i.description}</div>
        </div>
      ),
    },
    {
      key: 'kind', header: 'Kind',
      cell: (i) => (
        <span className="inline-flex gap-1">
          <Badge variant={KIND[i.incident_type]?.variant ?? 'neutral'} size="sm">{KIND[i.incident_type]?.label ?? i.incident_type}</Badge>
          {i.severity && needsSeverity(i.incident_type) && <Badge variant="neutral" size="sm">{SEVERITY[i.severity]}</Badge>}
        </span>
      ),
    },
    { key: 'when', header: 'Date', hideOnMobile: true, cell: (i) => <span className="text-body-sm text-ink-muted">{i.incident_date ? formatDate(i.incident_date) : '—'}</span> },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Behaviour"
        subtitle="Praise, concerns and disciplinary matters, per student. Parents see what's marked as shared."
        actions={canManage && <Button leftIcon={<PlusIcon className="h-4 w-4" />} onClick={() => setRecording(true)}>Record something</Button>}
      />
      <FilterChips options={SHOW} value={show} onChange={(k) => { setShow(k); setPage(1) }} />
      <DataTable
        data={list.data?.items ?? []}
        columns={columns}
        getRowId={(i) => i.id}
        loading={list.isLoading}
        pagination={list.data ? { page, pageSize: PAGE_SIZE, total: list.data.total } : undefined}
        onPageChange={setPage}
        onRowClick={setOpen}
        activeRowId={open?.id}
        emptyState={{ title: show ? 'Nothing here' : 'Nothing recorded yet', description: show ? 'Try another filter.' : 'Record praise as well as concerns: parents see both.' }}
        mobileCardRenderer={(i) => (
          <button type="button" onClick={() => setOpen(i)} className="w-full text-left p-4 border-b border-hairline flex items-center justify-between gap-3">
            <span className="min-w-0">
              <span className="block font-medium text-ink truncate">{i.beneficiary_name}</span>
              <span className="block text-caption text-ink-muted truncate">{i.category || i.description}</span>
            </span>
            <Badge variant={KIND[i.incident_type]?.variant ?? 'neutral'} size="sm">{KIND[i.incident_type]?.label}</Badge>
          </button>
        )}
      />
      {recording && <IncidentForm onClose={() => setRecording(false)} />}
      {open && <IncidentPanel incident={open} canManage={canManage} onClose={() => setOpen(null)} />}
    </div>
  )
}

function IncidentPanel({ incident, canManage, onClose }: { incident: Incident; canManage: boolean; onClose: () => void }) {
  const [editing, setEditing] = useState(false)
  const summary = useQuery({
    queryKey: ['discipline', 'summary', incident.beneficiary_id],
    queryFn: async () => (await api.get<{ total_incidents: number; total_points: number; incidents_by_type: Record<string, number> }>(
      `/education/discipline/students/${incident.beneficiary_id}/summary`)).data,
  })
  if (editing) return <IncidentForm incident={incident} onClose={onClose} />
  const s = summary.data
  return (
    <Modal open onClose={onClose} title={incident.beneficiary_name ?? 'Student'}>
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={KIND[incident.incident_type]?.variant ?? 'neutral'}>{KIND[incident.incident_type]?.label}</Badge>
          {incident.severity && needsSeverity(incident.incident_type) && <Badge variant="neutral">{SEVERITY[incident.severity]}</Badge>}
          <span className="text-body-sm text-ink-muted">{incident.incident_date ? formatDate(incident.incident_date) : ''}</span>
        </div>
        <div>
          {incident.category && <p className="text-body font-medium text-ink">{incident.category}</p>}
          <p className="text-body text-ink whitespace-pre-line">{incident.description}</p>
        </div>
        {incident.action_taken && (
          <div><p className="text-caption text-ink-faint">What was done</p><p className="text-body-sm text-ink">{incident.action_taken}</p></div>
        )}
        <p className="text-caption text-ink-muted">{incident.is_visible_to_parent ? 'Parents can see this.' : 'Kept from parents.'}</p>
        {s && (
          <section aria-label="This student so far" className="rounded-card bg-surface-alt p-4">
            <p className="text-caption text-ink-faint mb-2">This student so far</p>
            <div className="flex flex-wrap gap-2">
              {KIND_ORDER.filter((k) => s.incidents_by_type[k]).map((k) => (
                <Badge key={k} variant={KIND[k].variant} size="sm">{s.incidents_by_type[k]} × {KIND[k].label}</Badge>
              ))}
              <span className="text-body-sm text-ink tabular">{s.total_points} points</span>
            </div>
          </section>
        )}
        {canManage && (
          <div className="pt-4 border-t border-hairline">
            <Button variant="secondary" onClick={() => setEditing(true)}>Correct this record</Button>
          </div>
        )}
      </div>
    </Modal>
  )
}

function IncidentForm({ incident, onClose }: { incident?: Incident; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [student, setStudent] = useState<{ id: string; name: string } | null>(
    incident ? { id: incident.beneficiary_id, name: incident.beneficiary_name ?? 'Student' } : null,
  )
  const [kind, setKind] = useState<Kind>(incident?.incident_type ?? 'commendation')
  const [category, setCategory] = useState(incident?.category ?? '')
  const [description, setDescription] = useState(incident?.description ?? '')
  const [severity, setSeverity] = useState<Severity>(incident?.severity ?? 'minor')
  const [action, setAction] = useState(incident?.action_taken ?? '')
  const [points, setPoints] = useState(String(incident?.points ?? 0))
  const [date, setDate] = useState(incident?.incident_date?.slice(0, 10) ?? new Date().toISOString().slice(0, 10))
  const [shared, setShared] = useState(incident?.is_visible_to_parent ?? true)

  const students = useQuery({
    queryKey: ['beneficiaries', 'discipline-search', search.trim()],
    queryFn: async () => (await api.get<{ items: Beneficiary[] }>('/beneficiaries', { params: { search: search.trim(), per_page: 8 } })).data.items,
    enabled: !student && search.trim().length >= 2,
  })

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        incident_type: kind, category: category.trim() || null, description: description.trim(),
        severity: needsSeverity(kind) ? severity : null, action_taken: action.trim() || null,
        points: Number(points) || 0, is_visible_to_parent: shared,
      }
      return incident
        ? api.patch(`/education/discipline/incidents/${incident.id}`, body)
        : api.post('/education/discipline/incidents', { ...body, beneficiary_id: student!.id, incident_date: date })
    },
    onSuccess: () => {
      toast.success(incident ? 'Record corrected' : 'Recorded')
      queryClient.invalidateQueries({ queryKey: ['discipline'] })
      onClose()
    },
    onError: (e) => toast.error(apiError(e) || "Couldn't save it"),
  })
  const ready = !!student && description.trim().length > 0

  return (
    <Modal open onClose={onClose} title={incident ? 'Correct this record' : 'Record something'} size="lg">
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (ready) save.mutate() }}>
        {student ? (
          <div className="flex items-center justify-between gap-2">
            <p className="text-body text-ink"><span className="text-caption text-ink-faint block">Student</span>{student.name}</p>
            {!incident && <Button type="button" size="sm" variant="ghost" onClick={() => setStudent(null)}>Change</Button>}
          </div>
        ) : (
          <div>
            <label htmlFor="discipline-student" className="block text-body-sm font-medium text-ink mb-1">Student</label>
            <input id="discipline-student" type="search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Name or admission number" className={field} autoFocus />
            <ul className="mt-1">
              {(students.data ?? []).map((b) => (
                <li key={b.id}>
                  <button type="button" onClick={() => setStudent({ id: b.id, name: b.full_name })}
                    className="w-full text-left px-3 py-2 rounded-button hover:bg-surface-alt text-body-sm text-ink">
                    {b.full_name} <span className="text-ink-muted font-mono">{b.entity_reference}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <fieldset>
          <legend className="block text-body-sm font-medium text-ink mb-1">Kind</legend>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Kind">
            {KIND_ORDER.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={kind === k} onClick={() => setKind(k)}
                className={clsx('px-3 py-1.5 rounded-chip border text-body-sm', kind === k ? 'border-accent bg-accent-soft text-accent' : 'border-hairline text-ink hover:bg-surface-alt')}>
                {KIND[k].label}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block"><span className="block text-body-sm font-medium text-ink mb-1">About</span>
            <input value={category} onChange={(e) => setCategory(e.target.value)} list="discipline-categories" className={field} placeholder="e.g. Leadership" />
            <datalist id="discipline-categories">{SUGGESTIONS[kind].map((c) => <option key={c} value={c} />)}</datalist>
          </label>
          {!incident && (
            <label className="block"><span className="block text-body-sm font-medium text-ink mb-1">Date</span>
              <input type="date" value={date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} className={field} />
            </label>
          )}
        </div>
        <label className="block"><span className="block text-body-sm font-medium text-ink mb-1">What happened</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} className={clsx(field, 'h-24 resize-none')} />
        </label>
        {needsSeverity(kind) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block"><span className="block text-body-sm font-medium text-ink mb-1">How serious</span>
              <select value={severity} onChange={(e) => setSeverity(e.target.value as Severity)} className={field}>
                {(Object.keys(SEVERITY) as Severity[]).map((s) => <option key={s} value={s}>{SEVERITY[s]}</option>)}
              </select>
            </label>
            <label className="block"><span className="block text-body-sm font-medium text-ink mb-1">What was done (optional)</span>
              <input value={action} onChange={(e) => setAction(e.target.value)} className={field} placeholder="e.g. Spoke to parent" />
            </label>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-4">
          <label className="inline-flex items-center gap-2 text-body-sm text-ink">Points
            <input type="number" value={points} onChange={(e) => setPoints(e.target.value)} className={clsx(field, 'w-24 tabular')} aria-label="Points" />
          </label>
          <label className="inline-flex items-center gap-2 text-body-sm text-ink">
            <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} /> Parents can see this
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-hairline">
          <Button type="button" variant="ghost" onClick={onClose} disabled={save.isPending}>Cancel</Button>
          <Button type="submit" disabled={!ready} isLoading={save.isPending}>{incident ? 'Save correction' : 'Record'}</Button>
        </div>
      </form>
    </Modal>
  )
}
